import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Buildings, CheckCircle, IdentificationCard, UploadSimple, Camera, X } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KINDS = [
  { key: "nric_front", label: "NRIC / Passport (Front)", hint: "Clear photo of the front side" },
  { key: "nric_back",  label: "NRIC / Passport (Back)",  hint: "Clear photo of the back side" },
  { key: "selfie",     label: "Selfie holding NRIC",     hint: "A recent selfie holding the NRIC beside your face" },
];

export default function PublicEkyc() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(null);
  const [camOpen, setCamOpen] = useState(false);

  const load = () => axios.get(`${API}/public/ekyc/${token}`)
    .then(r => setData(r.data))
    .catch(e => setError(e.response?.data?.detail || "Invalid or expired link"));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);

  const upload = async (kind, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) return toast.error("File too large (max 15 MB)");
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      await axios.post(`${API}/public/ekyc/${token}/upload`, fd);
      toast.success(`${file.name} uploaded`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(null); }
  };

  const onCameraCaptured = async (blob) => {
    setCamOpen(false);
    const file = new File([blob], `selfie-${Date.now()}.jpg`, { type: "image/jpeg" });
    await upload("selfie", file);
  };

  if (error) return <Shell><div className="p-10 text-center"><h1 className="text-2xl">Link invalid or expired</h1><p className="mt-2 text-muted-foreground">{error}</p></div></Shell>;
  if (!data) return <Shell><div className="p-10 text-sm text-muted-foreground">Loading…</div></Shell>;

  const allUploaded = data.uploaded.nric_front && data.uploaded.nric_back && data.uploaded.selfie;

  return (
    <Shell>
      <div className="max-w-3xl mx-auto p-6 md:p-12">
        <article className="border border-border bg-white">
          <header className="px-8 py-6 border-b border-border">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">eKYC verification</div>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">Hi {data.person_name},</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              To complete the statutory Know-Your-Customer records for <b>{data.company_name}</b>
              {data.ssm_no ? <> · SSM {data.ssm_no}</> : null}, please upload the three items below.
              You are registered as <b>{data.role}</b>.
            </p>
          </header>

          {allUploaded ? (
            <div className="px-8 py-10 text-center">
              <div className="inline-flex items-center gap-2 text-[#24A148] font-medium text-lg">
                <CheckCircle size={22} weight="fill" /> All documents received
              </div>
              <p className="mt-3 text-sm text-muted-foreground">
                Thank you. Your company secretary will now review your submission. You can safely close this page.
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                To replace any file, just upload again below.
              </p>
            </div>
          ) : null}

          <div className="px-8 py-6 space-y-4" data-testid="ekyc-upload-slots">
            {KINDS.map(k => {
              const done = data.uploaded[k.key];
              const isSelfie = k.key === "selfie";
              return (
                <div key={k.key} className={`border ${done ? "border-[#24A148]/60 bg-[#24A148]/5" : "border-border"} p-4 flex items-center gap-4`}>
                  <IdentificationCard size={28} weight={done ? "fill" : "duotone"}
                                      className={done ? "text-[#24A148]" : "text-muted-foreground"} />
                  <div className="flex-1">
                    <div className="font-medium">{k.label}</div>
                    <div className="text-xs text-muted-foreground">{k.hint}</div>
                    {done && <div className="text-[11px] font-mono uppercase tracking-widest text-[#24A148] mt-1">Uploaded ✓</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    {isSelfie && (
                      <button type="button"
                              onClick={() => setCamOpen(true)}
                              disabled={uploading === k.key}
                              className={`inline-flex items-center h-10 px-4 rounded-sm border border-border text-sm ${uploading === k.key ? "opacity-60" : "hover:bg-[#F5F5F5]"}`}
                              data-testid="ekyc-open-camera-btn">
                        <Camera size={14} weight="bold" className="mr-2" /> Use camera
                      </button>
                    )}
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*,application/pdf" hidden
                             onChange={e => { if (e.target.files?.[0]) upload(k.key, e.target.files[0]); e.target.value = ""; }} />
                      <span className={`inline-flex items-center h-10 px-4 rounded-sm border border-border text-sm ${uploading === k.key ? "opacity-60" : "hover:bg-[#F5F5F5]"}`}>
                        <UploadSimple size={14} weight="bold" className="mr-2" />
                        {uploading === k.key ? "Uploading…" : done ? "Replace" : "Upload"}
                      </span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>

          <footer className="px-8 py-4 border-t border-border text-[11px] text-muted-foreground">
            Uploaded files are stored securely and are only used to fulfil statutory KYC obligations.
          </footer>
        </article>
      </div>
      {camOpen && <CameraSelfie onClose={() => setCamOpen(false)} onCapture={onCameraCaptured} />}
    </Shell>
  );
}

function CameraSelfie({ onClose, onCapture }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState("");
  const [captured, setCaptured] = useState(null); // Blob

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 960 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setReady(true);
        }
      } catch (e) {
        setErr(e.message || "Camera access denied. Please allow camera permission and try again.");
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const snap = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 960;
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, w, h);
    c.toBlob(blob => {
      if (blob) setCaptured(blob);
    }, "image/jpeg", 0.9);
  };

  const retake = () => setCaptured(null);
  const use = () => { if (captured) onCapture(captured); };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm w-full max-w-2xl border border-border">
        <header className="flex items-center justify-between px-6 py-3 border-b border-border">
          <div className="font-mono text-xs uppercase tracking-widest">Take a selfie holding your NRIC</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-black" data-testid="ekyc-camera-close">
            <X size={18} weight="bold" />
          </button>
        </header>
        <div className="p-6">
          {err ? (
            <div className="border border-[#DA1E28]/40 bg-[#DA1E28]/5 p-4 text-sm text-[#DA1E28]">
              {err}
            </div>
          ) : (
            <div className="bg-[#0A0A0A] flex items-center justify-center min-h-[280px]" data-testid="ekyc-camera-stage">
              {captured ? (
                <img src={URL.createObjectURL(captured)} alt="captured selfie"
                     className="max-h-[420px] max-w-full object-contain" />
              ) : (
                <video ref={videoRef} playsInline muted autoPlay
                       className="max-h-[420px] max-w-full object-contain"
                       style={{ transform: "scaleX(-1)" }} />
              )}
              <canvas ref={canvasRef} hidden />
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-3 text-center">
            Face the camera, hold your NRIC / passport clearly next to your face, then click <b>Capture</b>.
          </p>
        </div>
        <footer className="flex justify-end gap-2 px-6 py-3 border-t border-border">
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          {captured ? (
            <>
              <Button variant="outline" className="rounded-sm" onClick={retake} data-testid="ekyc-camera-retake">Retake</Button>
              <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" onClick={use} data-testid="ekyc-camera-use">
                Use this selfie
              </Button>
            </>
          ) : (
            <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" disabled={!ready || !!err} onClick={snap}
                    data-testid="ekyc-camera-capture">
              <Camera size={14} weight="bold" className="mr-2" /> Capture
            </Button>
          )}
        </footer>
      </div>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <header className="border-b border-border bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-4 flex items-center gap-3">
          <Buildings size={20} weight="duotone" />
          <span className="font-mono uppercase tracking-[0.2em] text-xs">Specfield Management Services Sdn. Bhd.</span>
          <span className="ml-auto text-[11px] font-mono text-muted-foreground flex items-center gap-1">
            <IdentificationCard size={14} weight="duotone" /> eKYC portal
          </span>
        </div>
      </header>
      {children}
    </div>
  );
}
